"use client";

import { useState } from "react";

import { formatDateTime } from "@/lib/format";
import type { Connection, ConnectionField } from "@/lib/api/types";

import { SecretField } from "./secret-field";
import { MESSAGE_COLORS, StatusPill } from "./status-pill";

type Props = {
  connection: Connection;
  testing: boolean;
  /** Meldung der letzten Aktion in der Karte (z. B. Fehler beim Autorisieren), überschreibt die Statusmeldung. */
  notice: string | null;
  onSaveAndTest: (values: Record<string, string>) => void;
  onDisconnect: () => void;
  onAuthorize: () => void;
};

const control =
  "h-11 w-full min-w-0 rounded-md border border-field bg-white px-3 text-sm text-ink " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent";
const secondaryButton =
  "h-11 rounded-md border border-field bg-white px-3.5 text-sm focus-visible:outline-2 " +
  "focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60";

function initialDraft(connection: Connection): Record<string, string> {
  // Geheimnisse beginnen leer: Der gespeicherte Wert bleibt, bis jemand einen neuen eingibt
  return Object.fromEntries(connection.fields.map((f) => [f.key, f.secret ? "" : f.value]));
}

export function SourceCard({ connection, testing, notice, onSaveAndTest, onDisconnect, onAuthorize }: Props) {
  const [draft, setDraft] = useState(() => initialDraft(connection));
  const { source } = connection;

  const set = (key: string, value: string) => setDraft((current) => ({ ...current, [key]: value }));

  const submit = () => {
    const values: Record<string, string> = {};
    for (const field of connection.fields) {
      const value = draft[field.key] ?? "";
      // Ein leeres Geheimnis bedeutet „unverändert“ und wird gar nicht erst gesendet
      if (field.secret && value.trim() === "") continue;
      values[field.key] = value;
    }
    onSaveAndTest(values);
  };

  const status = testing ? "testing" : connection.status;
  const message = testing
    ? "Verbindung wird serverseitig getestet …"
    : (notice ?? connection.status_message);
  const lastTested =
    !testing && !notice && connection.last_tested_at
      ? ` · zuletzt getestet ${formatDateTime(connection.last_tested_at)}`
      : "";

  return (
    <section
      aria-labelledby={`${source}-title`}
      className="flex flex-col rounded-lg border border-line bg-white"
    >
      <div className="flex items-start justify-between gap-3 border-b border-neutral-bg px-5 py-4">
        <div className="flex flex-col gap-0.5">
          <h2 id={`${source}-title`} className="text-base font-semibold">
            {connection.name}
          </h2>
          <span className="text-[13px] text-muted">
            {connection.auth} · {connection.use}
          </span>
        </div>
        <StatusPill status={status} />
      </div>

      <div className="flex flex-col gap-3.5 px-5 py-4">
        {connection.oauth && (
          <div className="flex flex-wrap items-center justify-between gap-2.5 rounded-md bg-surface px-3 py-2.5">
            <span className="text-sm">
              {connection.oauth_authorized
                ? `Autorisiert als ${connection.oauth_account ?? "Google-Konto"}`
                : "Kein Google-Konto verbunden"}
            </span>
            <button type="button" onClick={onAuthorize} className={`${secondaryButton} text-[13px]`}>
              {connection.oauth_authorized ? "Neu autorisieren" : "Mit Google verbinden"}
            </button>
          </div>
        )}

        {connection.fields.map((field) => (
          <Field
            key={field.key}
            id={`${source}-${field.key}`}
            field={field}
            value={draft[field.key] ?? ""}
            onChange={(value) => set(field.key, value)}
          />
        ))}

        <div aria-live="polite" className={`text-[13px] ${MESSAGE_COLORS[status]}`}>
          {message}
          {lastTested}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 rounded-b-lg border-t border-neutral-bg bg-[#fafbfa] px-5 py-3.5">
        <button
          type="button"
          onClick={submit}
          disabled={testing}
          className="h-11 rounded-md bg-accent px-3.5 text-sm font-medium text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
        >
          Testen &amp; speichern
        </button>
        <button
          type="button"
          onClick={onDisconnect}
          disabled={testing}
          className={`${secondaryButton} text-danger`}
        >
          Trennen
        </button>
        <span className="flex-1" />
        <a
          href={connection.docs_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-11 items-center text-[13px] text-accent underline"
        >
          Wo finde ich das?
        </a>
      </div>
    </section>
  );
}

function Field({
  id,
  field,
  value,
  onChange,
}: {
  id: string;
  field: ConnectionField;
  value: string;
  onChange: (value: string) => void;
}) {
  const hintId = field.hint ? `${id}-hint` : undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-medium">
        {field.label}
        {field.required && <span className="sr-only"> (Pflichtfeld)</span>}
      </label>
      {field.kind === "select" ? (
        <select
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-describedby={hintId}
          className={control}
        >
          <option value="">Bitte wählen</option>
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : field.secret ? (
        <SecretField
          id={id}
          label={field.label}
          value={value}
          onChange={onChange}
          placeholder={field.placeholder}
          describedBy={hintId}
          storedMask={field.has_value ? field.value : null}
        />
      ) : (
        <input
          id={id}
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={field.placeholder}
          autoComplete="off"
          spellCheck={false}
          aria-describedby={hintId}
          className={`${control} font-mono text-[13px]`}
        />
      )}
      {field.hint && (
        <span id={hintId} className="text-xs leading-normal text-muted">
          {field.hint}
        </span>
      )}
    </div>
  );
}
